import { useCallback, useEffect, useRef, useState } from 'react'

export interface UseTopScrollbarResult {
  /** Attach to the thin proxy scrollbar rendered above the content. */
  scrollbarRef: React.RefObject<HTMLDivElement>
  /** Attach to the element that actually scrolls horizontally. */
  contentRef: React.RefObject<HTMLDivElement>
  /** The content's `scrollWidth` — use as the width of the proxy's spacer. */
  contentWidth: number
  /** Whether the content is actually wider than its box (1px tolerance for sub-pixel layout). */
  hasOverflow: boolean
  /**
   * Thickness in px of a classic scrollbar on this platform, or `0` where scrollbars are drawn as
   * overlays (macOS, iOS, Android) and reserve no layout space.
   */
  scrollbarThickness: number
}

/**
 * How much height a horizontal scrollbar takes out of a scroll container on this platform.
 *
 * `0` means overlay scrollbars: the bar is painted over the content and appears only while
 * scrolling. A proxy scrollbar is useless there — it would be drawn over a strip barely a pixel
 * tall — so callers should skip it and leave the content's own bar alone.
 */
export function measureScrollbarThickness(): number {
  if (typeof document === 'undefined') return 0

  const probe = document.createElement('div')
  probe.setAttribute('aria-hidden', 'true')
  // Every box property is pinned so the reading can't be skewed by a global reset (a `*` rule
  // adding padding/border, or `box-sizing: border-box`) in whichever app mounts this.
  probe.style.cssText =
    'position:absolute;top:-9999px;width:100px;height:100px;overflow-x:scroll;' +
    'padding:0;border:0;box-sizing:content-box'

  const inner = document.createElement('div')
  inner.style.cssText = 'width:200px;height:1px'
  probe.appendChild(inner)

  document.body.appendChild(probe)
  const thickness = probe.offsetHeight - probe.clientHeight
  document.body.removeChild(probe)

  return thickness
}

let cachedScrollbarThickness: number | null = null

/**
 * {@link measureScrollbarThickness}, measured once per session.
 *
 * The probe forces a synchronous layout, and a page can mount several of these tables at once, so
 * the uncached version would cost one style recalculation per mount for a value that cannot change
 * without a browser or OS restart.
 */
export function getScrollbarThickness(): number {
  if (cachedScrollbarThickness === null) {
    cachedScrollbarThickness = measureScrollbarThickness()
  }
  return cachedScrollbarThickness
}

/**
 * Drives a proxy scrollbar rendered *above* a horizontally-scrolling element, by mirroring
 * `scrollLeft` between the two.
 *
 * This exists to replace the old `transform: scaleY(-1)` + counter-flipped children trick, which
 * put a scroll container's horizontal scrollbar on top by turning it upside down. That flip moved
 * the whole subtree onto a composited layer, rasterized with grayscale rather than subpixel
 * antialiasing and resampled whenever the scrollbar's reserved height landed on a fractional
 * device-pixel offset — which made table text visibly soft on desktop (#21853). Mirroring scroll
 * offsets keeps the scrollbar-on-top affordance with no transform anywhere in the subtree.
 *
 * Pass `enabled: false` to make this inert (no listeners, no observer) for callers that only want
 * the top scrollbar conditionally.
 */
export function useTopScrollbar(enabled = true): UseTopScrollbarResult {
  const scrollbarRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  const [contentWidth, setContentWidth] = useState(0)
  const [hasOverflow, setHasOverflow] = useState(false)
  // Platform-constant, so it is measured once per session and shared across every mount.
  const [scrollbarThickness] = useState(getScrollbarThickness)

  const measure = useCallback(() => {
    const content = contentRef.current
    if (!content) return

    const { scrollWidth, clientWidth } = content
    setContentWidth(scrollWidth)
    setHasOverflow(scrollWidth > clientWidth + 1)
  }, [])

  useEffect(() => {
    if (!enabled) return

    const content = contentRef.current
    const scrollbar = scrollbarRef.current
    if (!content || !scrollbar) return

    // Writing `to.scrollLeft` emits a `scroll` event on `to`, so the pair has to settle by itself.
    // The in-sync test is a sub-pixel *tolerance*, not strict equality: browsers snap scrollLeft to
    // device pixels and clamp it to the element's own max offset, so the two boxes can each hold a
    // slightly different value that is already a fixed point for them — under `===` that ping-pongs
    // forever, one event per frame, on fractional-DPR displays. A "mirroring now" flag would stop
    // it, but at the cost of swallowing genuine scroll events that coalesce during a fast drag; a
    // tolerance stops it without dropping anything, and a sub-pixel divergence is imperceptible.
    const mirror = (from: HTMLDivElement, to: HTMLDivElement) => () => {
      if (Math.abs(to.scrollLeft - from.scrollLeft) < 1) return
      to.scrollLeft = from.scrollLeft
    }

    const onContentScroll = mirror(content, scrollbar)
    const onScrollbarScroll = mirror(scrollbar, content)

    content.addEventListener('scroll', onContentScroll, { passive: true })
    scrollbar.addEventListener('scroll', onScrollbarScroll, { passive: true })

    return () => {
      content.removeEventListener('scroll', onContentScroll)
      scrollbar.removeEventListener('scroll', onScrollbarScroll)
    }
  }, [enabled])

  useEffect(() => {
    if (!enabled) return

    const content = contentRef.current
    if (!content) return

    measure()

    // Observe the scroller *and* its first child: the scroller is pinned to 100% of the card, so
    // only the child (the table, sized `fit-content`) changes width when columns are toggled or a
    // long cell value arrives.
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => measure())
      observer.observe(content)
      if (content.firstElementChild) observer.observe(content.firstElementChild)

      return () => observer.disconnect()
    }

    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [enabled, measure])

  return { scrollbarRef, contentRef, contentWidth, hasOverflow, scrollbarThickness }
}
